import React, { useState, useMemo } from "react";
import { Text, cn } from "@musa.tech/design-system";
import { EvolutionChart } from "./EvolutionChart";
import { SegmentedControl } from "./SegmentedControl";
import { useEvolutionChartData, GranularityType, MetricType } from "@/hooks/useEvolutionChartData";
import { getMaterialColorByRank } from "./collectionsEvolutionChartColors";
import {
  aggregateByMaterialPerDay,
  sortMaterialIdsByTotalValueDesc,
  toStackOrder,
} from "./collectionsEvolutionChartAggregation";
import type { FilterState } from "@/hooks/useFilters";
import { COLLECTIONS_EVOLUTION_CHART_DISABLED } from "@/config/charts";
import type { DashboardByMaterialPerDay } from "@/integrations/agreements/client";

interface CollectionsEvolutionChartProps {
  filters?: FilterState;
  /** Quando fornecido (dados da API Dashboard), usa em vez do Supabase */
  dashboardByMaterialPerDay?: DashboardByMaterialPerDay[] | null;
}

const GRANULARITY_OPTIONS = [
  { value: "day", label: "Diário" },
  { value: "week", label: "Semanal" },
  { value: "month", label: "Mensal" },
];

const METRIC_OPTIONS = [
  { value: "units", label: "Unidades" },
  // Sem "(kg)": no Dashboard a unidade de peso varia com a grandeza.
  { value: "weight", label: "Peso" },
  { value: "volume", label: "Volume (L)" },
];

interface ChartControlsProps {
  granularity: GranularityType;
  onGranularityChange: (value: GranularityType) => void;
  metric: MetricType;
  onMetricChange: (value: MetricType) => void;
  disabled?: boolean;
}

/** Linha "Período: [pílulas] Métrica: [pílulas]" — Figma node 124:13045. */
const ChartControls = ({
  granularity,
  onGranularityChange,
  metric,
  onMetricChange,
  disabled,
}: ChartControlsProps) => (
  <div
    className={cn(
      "flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center",
      disabled && "pointer-events-none opacity-50",
    )}
  >
    <div className="flex items-center gap-2">
      <Text variant="label" size="small" className="whitespace-nowrap text-neutral-600">
        Período:
      </Text>
      <SegmentedControl
        value={granularity}
        onChange={onGranularityChange}
        options={GRANULARITY_OPTIONS}
        aria-label="Selecionar período de agrupamento"
      />
    </div>
    <div className="flex items-center gap-2">
      <Text variant="label" size="small" className="whitespace-nowrap text-neutral-600">
        Métrica:
      </Text>
      <SegmentedControl
        value={metric}
        onChange={onMetricChange}
        options={METRIC_OPTIONS}
        aria-label="Selecionar métrica de coleta"
      />
    </div>
  </div>
);

export const CollectionsEvolutionChart = ({
  filters,
  dashboardByMaterialPerDay,
}: CollectionsEvolutionChartProps) => {
  const [granularity, setGranularity] = useState<GranularityType>("day");
  const [metric, setMetric] = useState<MetricType>("weight");

  const { points, namesById } = useMemo(
    () =>
      dashboardByMaterialPerDay?.length
        ? aggregateByMaterialPerDay(dashboardByMaterialPerDay, granularity, metric)
        : { points: [], namesById: {} },
    [dashboardByMaterialPerDay, granularity, metric],
  );

  const { isLoading } = useEvolutionChartData(
    dashboardByMaterialPerDay ? undefined : filters,
    granularity,
    metric,
  );

  const header = (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <Text className="text-xl font-light leading-5 text-black">
        Coletas ao longo do tempo
      </Text>
      <ChartControls
        granularity={granularity}
        onGranularityChange={setGranularity}
        metric={metric}
        onMetricChange={setMetric}
        disabled={COLLECTIONS_EVOLUTION_CHART_DISABLED}
      />
    </div>
  );

  if (isLoading) {
    return (
      <div className="w-full rounded-2xl bg-white p-6">
        {header}
        <div className="py-8 text-center">
          <Text variant="body" size="small" className="text-neutral-600">
            Carregando dados...
          </Text>
        </div>
      </div>
    );
  }

  if (points.length === 0) {
    return (
      <div className="w-full rounded-2xl bg-white p-6">
        {header}
        <div className="py-8 text-center">
          <Text variant="body" size="small" className="mb-2 text-neutral-600">
            Nenhum dado disponível para exibir
          </Text>
          <Text className="text-xs leading-4 text-neutral-500">
            Dados serão exibidos quando houver coletas registradas
          </Text>
        </div>
      </div>
    );
  }

  const materialIdsUnordered = Array.from(
    points.reduce((acc, point) => {
      Object.keys(point).forEach((key) => {
        if (key !== "period" && Number(point[key]) > 0) acc.add(key);
      });
      return acc;
    }, new Set<string>()),
  );
  // Legenda: maior total primeiro (Figma node 233:2525).
  const materialIdsWithData = sortMaterialIdsByTotalValueDesc(points, materialIdsUnordered);
  // Pilha: inversa da legenda — maior no topo, menor na base (ver toStackOrder).
  const stackOrder = toStackOrder(materialIdsWithData);

  return (
    <div className="relative w-full overflow-hidden rounded-2xl bg-white p-6">
      {header}

      <div
        className={cn(
          "mt-4 w-full",
          COLLECTIONS_EVOLUTION_CHART_DISABLED && "pointer-events-none opacity-50",
        )}
        style={{ height: "clamp(250px, 45vh, 326px)" }}
      >
        <EvolutionChart
          data={points}
          namesById={namesById}
          materialOrder={stackOrder}
          granularity={granularity}
          metric={metric}
        />
      </div>

      <div
        className={cn(
          "mt-4 flex flex-wrap justify-center gap-4",
          COLLECTIONS_EVOLUTION_CHART_DISABLED && "pointer-events-none opacity-50",
        )}
      >
        {materialIdsWithData.map((materialId, rank) => {
          const name = namesById[materialId] ?? "Outros";
          return (
            <div key={materialId} className="flex max-w-[240px] items-center gap-1">
              <div
                className="size-5 shrink-0 rounded-lg"
                style={{ backgroundColor: getMaterialColorByRank(rank) }}
              />
              {/* truncate, não whitespace-nowrap: o nome do material vem do
                  cadastro (registry_materials) e às vezes é uma descrição
                  longa, não um rótulo curto — sem limite, uma única legenda
                  vazava do card inteiro. */}
              <Text
                title={name}
                className="min-w-0 truncate text-xs leading-5 text-neutral-500"
              >
                {name}
              </Text>
            </div>
          );
        })}
      </div>

      {COLLECTIONS_EVOLUTION_CHART_DISABLED && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/80 backdrop-blur-sm">
          <div className="px-4 py-8 text-center">
            <Text variant="label" size="small" className="mb-1 text-neutral-700">
              Gráfico em manutenção
            </Text>
            <Text variant="body" size="small" className="text-neutral-600">
              Em breve voltará a exibir os dados normalmente.
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};
